/**
 * WorkerDetail — one contractor worker, and whether they may work.
 *
 * The eligibility panel is the point of the page. It is opened by a supervisor
 * who has just been told a worker cannot be added to a permit and needs to know
 * what to fix — so the blockers, their detail and the action come first, before
 * the profile.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, User, ShieldCheck, GraduationCap, Stethoscope, Loader2,
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import EligibilityBadge from '../../components/EligibilityBadge';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { fetchWorkers, fetchWorkerEligibility } from '../../services/contractorApi';

const TABS = [
  { key: 'eligibility', label: 'Eligibility', Icon: ShieldCheck },
  { key: 'profile', label: 'Profile', Icon: User },
  { key: 'induction', label: 'Induction & training', Icon: GraduationCap },
  { key: 'medical', label: 'Medical', Icon: Stethoscope },
];

function Row({ label, value }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-40 flex-shrink-0 text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value || '—'}</dd>
    </div>
  );
}

function dateOnly(v) {
  return v ? String(v).slice(0, 10) : null;
}

export default function WorkerDetail({ user, onLogout }) {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const { showError } = useAlert();
  const { activeSiteId } = useSite();

  const [tab, setTab] = useState('eligibility');
  const [worker, setWorker] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [wRes, eRes] = await Promise.all([
        fetchWorkers({}, { signal }),
        fetchWorkerEligibility(workerId, { siteId: activeSiteId }, { signal })
          .catch(() => null),
      ]);
      setWorker((wRes.workers || []).find((w) => w.id === workerId) || null);
      setEligibility(eRes);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      showError('Could not load the worker.');
    } finally {
      setLoading(false);
    }
  }, [workerId, activeSiteId, showError]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center text-gray-500">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          <p className="mt-2 text-sm">Loading worker…</p>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-600">
            This worker does not exist, or is not at a site you can access.
          </p>
          <button onClick={() => navigate('/contractors')}
                  className="mt-3 text-sm font-medium text-blue-600 hover:underline">
            Back to contractors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate(
            worker.contractor_company_id
              ? `/contractors/${worker.contractor_company_id}`
              : '/contractors')}
                  className="mb-2 inline-flex items-center gap-1 text-sm
                             text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-4 w-4" />
            {worker.contractor_company || 'Contractors'}
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{worker.name}</h1>
            <EligibilityBadge result={eligibility} defaultOpen={false} />
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {[worker.trade, worker.badge_no && `Badge ${worker.badge_no}`,
              worker.contractor_company].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="mb-5 flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium
                          ${tab === key
                            ? 'border-b-2 border-blue-600 text-blue-700'
                            : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === 'eligibility' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-1 font-semibold text-gray-900">
              May this worker perform controlled work?
            </h2>
            <p className="mb-4 text-xs text-gray-500">
              Evaluated against the contractor’s requirement template for
              {activeSiteId ? ' the selected site' : ' any site'}. This is the
              same answer PTW uses, so what you see here is what the permit gate
              will do.
            </p>
            {eligibility
              ? <EligibilityBadge result={eligibility} defaultOpen />
              : <p className="text-sm text-gray-500">
                  Eligibility could not be evaluated.
                </p>}
          </div>
        )}

        {tab === 'profile' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <dl className="text-sm">
              <Row label="Name" value={worker.name} />
              <Row label="Contractor" value={worker.contractor_company} />
              <Row label="Trade" value={worker.trade} />
              <Row label="Badge number" value={worker.badge_no} />
              <Row label="Phone" value={worker.phone} />
              <Row label="Email" value={worker.email} />
              <Row label="Site" value={worker.site_name
                || (worker.site_id ? worker.site_id : 'All sites')} />
              <Row label="Status" value={worker.status} />
              {/* The ID proof is returned masked by the API and stays masked
                  here — it is identity evidence, not a display field. */}
              <Row label="ID proof"
                   value={worker.id_proof_type
                     ? `${worker.id_proof_type} · ${worker.id_proof_number || 'on file'}`
                     : null} />
            </dl>
          </div>
        )}

        {tab === 'induction' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-900">Induction</h2>
              <dl className="text-sm">
                <Row label="Completed"
                     value={worker.induction_done ? 'Yes' : 'No'} />
                <Row label="Completed on"
                     value={dateOnly(worker.induction_date)} />
                <Row label="Valid until"
                     value={dateOnly(worker.induction_valid_to)
                       || (worker.induction_done
                         ? 'No expiry recorded' : null)} />
                <Row label="Gate pass valid to"
                     value={dateOnly(worker.gate_pass_valid_to)} />
              </dl>
              <p className="mt-3 text-xs text-gray-500">
                Induction is delivered as a training course, so it is assigned,
                completed and expired through the same machinery as any other
                training — including bulk assignment for a large workforce.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-900">Certifications</h2>
              {(worker.certifications || []).length === 0 ? (
                <p className="text-sm text-gray-500">None recorded.</p>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm">
                  {worker.certifications.map((c, i) => (
                    <li key={`${c.name}-${i}`} className="flex flex-wrap
                                                          items-center gap-3 py-2">
                      <span className="font-medium text-gray-900">{c.name}</span>
                      {c.certificate_number && (
                        <span className="text-xs text-gray-500">
                          #{c.certificate_number}
                        </span>
                      )}
                      {c.issuing_authority && (
                        <span className="text-xs text-gray-500">
                          {c.issuing_authority}
                        </span>
                      )}
                      {c.expiry_date && (
                        <span className="ml-auto text-xs text-gray-600">
                          expires {dateOnly(c.expiry_date)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'medical' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">Medical fitness</h2>
            <dl className="text-sm">
              <Row label="Fitness valid until"
                   value={dateOnly(worker.medical_valid_to)} />
            </dl>
            <p className="mt-3 text-xs text-gray-500">
              A validity date and nothing more. Clinical findings live in the
              occupational-health module behind its own access control, and
              eligibility never reads them — it only needs to know whether a
              fitness certificate is current.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
